import * as React from 'react';
import BPromise from 'bluebird';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { Grid2 } from 'Src/components/Grid/Grid2';
import { Loader } from 'Src/components/Loader/Loader';
import { ActionItem } from 'Src/components/ActionsButton/ActionsButton';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import {
  getEventEndTime,
  getEventGender,
  getEventStartTime,
  getEventStatus,
  getEventSubtype,
  getEventType,
  getEventVenue,
  getEventVenueType
} from 'Src/helpers/accessor/accessor';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import {
  getAllSuperAdminSchoolEvents,
  getSuperAdminSchoolEvents,
  getSuperAdminSchoolEventsCount
} from 'Src/helpers/service/superadmin/events';
import {
  getSelectOptionForEventTypes,
  getSelectOptionForEventVenueType,
  getSelectOptionForTournamentEventGender,
  getSelectOptionForTournamentEventStatus,
  getSelectOptionsForSubTypeEvent
} from 'Src/helpers/table/select';
import './SuperAdminSchoolEvents.css';

function getCurrentAcademicYearStartYear(): number {
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth(); // 0 = January, 8 = September

  return currentMonth >= 8 ? currentYear : currentYear - 1;
}

function getCurrentAcademicYearLabel(): string {
  const startYear = getCurrentAcademicYearStartYear();

  return `01 September ${startYear} - 31 August ${startYear + 1}`;
}

function isDateFilterApplied(filters: any, field: string): boolean {
  const filter = filters[field];

  return typeof filter !== 'undefined' && (filter.from !== '' || filter.to !== '');
}

function getCurrentAcademicYearWhere(): any {
  const startYear = getCurrentAcademicYearStartYear();

  return {
    startTime: {
      $gte: `${startYear}-09-01T06:00:00.000Z`,
      $lte: `${startYear + 1}-08-31T05:59:00.000Z`
    }
  };
}

function getEventGeneratedNameForSchool(event: any, schoolId: string): string {
  const generatedNames = event.generatedNames || {};

  return generatedNames[schoolId] || event.name || event.title || '';
}

function getEventsWhere(filters: any): any {
  const where = getServerFieldSectionWhere(filters, { isCorrectTime: true });
  const isStartTimeFilterApplied = isDateFilterApplied(filters, 'startTime');
  const isEndTimeFilterApplied = isDateFilterApplied(filters, 'endTime');

  if (!isStartTimeFilterApplied && !isEndTimeFilterApplied) {
    return {
      ...where,
      ...getCurrentAcademicYearWhere()
    };
  }

  return where;
}

function getColumns(schoolId: string): ColumnDefinition[] {
  return [
    {
      text: 'Event',
      field: 'generatedName',
      isSort: false,
      type: FILTER_TYPE.NONE,
      accessor: event => getEventGeneratedNameForSchool(event, schoolId)
    },
    {
      text: 'Start time',
      field: 'startTime',
      isSort: true,
      type: FILTER_TYPE.DATE_INTERVAL,
      accessor: getEventStartTime
    },
    {
      text: 'Finish time',
      field: 'endTime',
      isSort: true,
      type: FILTER_TYPE.DATE_INTERVAL,
      accessor: getEventEndTime
    },
    {
      text: 'Type',
      field: 'subtype',
      isSort: false,
      type: FILTER_TYPE.SELECT,
      accessor: getEventSubtype
    },
    {
      text: 'Kind',
      field: 'eventType',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getEventType
    },
    {
      text: 'Status',
      field: 'status',
      isSort: true,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getEventStatus
    },
    {
      text: 'Gender',
      field: 'gender',
      isSort: true,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getEventGender
    },
    {
      text: 'Home/Away',
      field: 'venueType',
      isSort: false,
      type: FILTER_TYPE.MULTISELECT,
      accessor: getEventVenueType
    },
    {
      text: 'Venue',
      field: 'venue',
      isSort: false,
      type: FILTER_TYPE.NONE,
      accessor: getEventVenue
    }
  ];
}

interface Props {
  schoolId: string;
  history: History;
  location: Location;
}

interface State {
  items: any[];
  filterOptions: any;
  currentPage: number;
  selectedItems: any[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  filters: any;
  isDataFiltered: boolean;
  isLoading: boolean;
  total: number;
  basePath: string;
  isSelectAllChecked: boolean;
  errorMessage: string;
}

export class SuperAdminSchoolEvents extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      items: [],
      filterOptions: this.getFilterOptions(),
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      filters: {},
      isDataFiltered: false,
      isLoading: false,
      total: 0,
      basePath: '',
      isSelectAllChecked: false,
      errorMessage: ''
    };
  }

  componentDidMount() {
    this.setState({ isLoading: true, errorMessage: '' });
    this.setItems();
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.location.search !== this.props.location.search || nextProps.schoolId !== this.props.schoolId) {
      this.setState({ isLoading: true, errorMessage: '' });
      this.setItems(nextProps);
    }
  }

  getFilterOptions() {
    return {
      subtype: getSelectOptionsForSubTypeEvent(),
      eventType: getSelectOptionForEventTypes(),
      status: getSelectOptionForTournamentEventStatus(),
      gender: getSelectOptionForTournamentEventGender(),
      venueType: getSelectOptionForEventVenueType()
    };
  }

  setItems(props: Props = this.props) {
    const { history, schoolId } = props;
    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':') as [string, TABLE_SORT_DIRECTION];
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';
    const columns = getColumns(schoolId);
    const filters = getFilters2(columns, search);
    const isShowFilter = isFilterExist2(filters);
    const isDataFiltered = isFilterExist2(filters);
    const where = getEventsWhere(filters);
    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : 'startTime DESC';
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    return BPromise.all([
      getSuperAdminSchoolEventsCount(schoolId, where),
      getSuperAdminSchoolEvents(schoolId, serverQueryFilter)
    ])
      .then(([countObj, items]) => {
        this.setState({
          currentPage: page,
          sortDirection,
          sortColumnsName,
          isShowFilter,
          isDataFiltered,
          filters,
          items,
          total: countObj.count,
          basePath: history.location.pathname,
          isLoading: false,
          errorMessage: ''
        });

        return true;
      })
      .catch(error => {
        console.error('Can not load superadmin school events', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not load events. Please check the session and try again.'
        });
      });
  }

  setCurrentPageParams = (currentPage: number): void => {
    const search: string[] = [];
    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== FIRST_PAGE) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];
    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    const selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({ selectedItems: selectedItemsUpdated });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;
    const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

    this.setState({
      selectedItems: [...selectedItems, ...itemsFiltered],
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;
    const where = getEventsWhere(filters);

    this.setState({ isLoading: true, errorMessage: '' });

    getAllSuperAdminSchoolEvents(this.props.schoolId, where)
      .then(items => {
        const itemsFiltered = items.filter(item => selectedItems.every(selectedItem => selectedItem.id !== item.id));

        this.setState({
          selectedItems: [...selectedItems, ...itemsFiltered],
          isSelectAllChecked: true,
          isLoading: false
        });
      })
      .catch(error => {
        console.error('Can not select all superadmin school events', error);
        this.setState({
          isLoading: false,
          errorMessage: 'Can not select all events. Please try again.'
        });
      });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;
    const order = getOrder(sortField, sortDirection, sortColumnsName);
    const search: string[] = [`order=${sortField}:${order}`];

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];
    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
      }
    } else {
      const columns = getColumns(this.props.schoolId);
      const filter = columns.find(col => col.field === filterField);
      const filterType = filter && filter.type;

      switch (filterType) {
        case FILTER_TYPE.MULTISELECT: {
          const selectOptions = event.target.options;
          const value = [];

          for (let i = 0; i < selectOptions.length; i++) {
            if (selectOptions[i].selected) {
              value.push(selectOptions[i].value);
            }
          }

          nextFilters = {
            ...nextFilters,
            [filterField]: value
          };
          break;
        }
        default:
          nextFilters = {
            ...nextFilters,
            [filterField]: filterValue
          };
          break;
      }
    }

    this.setState({ filters: nextFilters });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    const search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    if (isFilterExist2(filters)) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    this.setState({
      isShowFilter: !this.state.isShowFilter
    });
  };

  render() {
    const {
      currentPage,
      errorMessage,
      filters,
      filterOptions,
      isDataFiltered,
      isLoading,
      isSelectAllChecked,
      isShowFilter,
      items,
      selectedItems,
      sortColumnsName,
      sortDirection,
      total
    } = this.state;

    const columns = getColumns(this.props.schoolId);

    const actionItems: ActionItem[] = [
      {
        itemText: 'View event',
        isActive: false,
        onItemClick: () => undefined
      }
    ];

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="SuperAdminSchoolEvents">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h4 className="mb-1">Events</h4>
            <div className="text-muted">Read-only event list migrated onto the shared Grid2.</div>
            <div className="text-muted small">
              Showing current academic year by default: {getCurrentAcademicYearLabel()}. Use Start time or Finish time
              filters to view another range.
            </div>
          </div>
          <button className="btn btn-outline-secondary" onClick={() => this.setItems()}>
            Refresh
          </button>
        </div>

        {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

        <Grid2
          dataItems={items}
          filters={filters}
          currentPage={currentPage}
          total={total}
          isSelectAllChecked={isSelectAllChecked}
          selectedItemsCount={selectedItems.length}
          isDataFiltered={isDataFiltered}
          sortDirection={sortDirection}
          sortColumnsName={sortColumnsName}
          isShowFilter={isShowFilter}
          dataItemsSelected={selectedItems}
          columns={columns}
          actionItems={actionItems}
          options={filterOptions}
          onItemClick={this.onItemClick}
          onSortClick={this.onTableSortClick}
          onApplyFilterClick={this.onApplyFilterClick}
          onClearFilterClick={this.onClearFilterClick}
          onTableFilterChange={this.onTableFilterChange}
          onTableFilterClick={this.onTableFilterClick}
          setCurrentPageParams={this.setCurrentPageParams}
          onSelectAllClick={this.onSelectAllClick}
          onSelectAllOnPageClick={this.onSelectAllOnPageClick}
          onUnselectAllClick={this.onUnselectAllClick}
          isCSV={true}
        />
      </div>
    );
  }
}

